import React, { useEffect, useState } from 'react';
import { NumberInput } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import classes from './sceneCard.module.css';

type DurationChipProps = {
    /** Seconds the scene plays for. */
    seconds: number;
    /**
     * When provided the chip becomes an editable input that commits on blur or
     * Enter (not on every keystroke); otherwise it's a static pill.
     */
    onCommit?: (seconds: number) => void;
};

/**
 * How long a scene shows within a playlist. Read-only it's a compact pill on the
 * scene tile; editable it's a small number input with a clock icon and an `s`
 * suffix. {@see SceneCard} places it over the bottom-left of the tile.
 */
const DurationChip: React.FC<DurationChipProps> = ({ seconds, onCommit }) => {
    const [value, setValue] = useState<number | string>(seconds);

    // Keep the draft in sync when the persisted value changes underneath us.
    useEffect(() => {
        setValue(seconds);
    }, [seconds]);

    if (!onCommit) {
        return (
            <span className={classes.durationPill}>
                <IconClock size={13} />
                {seconds}s
            </span>
        );
    }

    const commit = () => {
        const next = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(next) && next !== seconds) {
            onCommit(next);
        } else if (!Number.isFinite(next)) {
            setValue(seconds);
        }
    };

    return (
        <NumberInput
            value={value}
            onChange={setValue}
            onBlur={commit}
            onKeyDown={event => {
                if (event.key === 'Enter') event.currentTarget.blur();
            }}
            min={0}
            step={5}
            clampBehavior="strict"
            hideControls
            size="xs"
            w={92}
            suffix="s"
            leftSection={<IconClock size={14} />}
            aria-label="Scene duration in seconds"
        />
    );
};

export default DurationChip;
